import { useCallback, useEffect, useState, useContext } from 'react'
import { useWeb3React } from '@web3-react/core'
import { approve, investPool, listingClaim, vestingClaim } from '../utils/callHelpers'
import { getAddress } from '../utils/addressHelpers'
import { getInfoContract, getPoolContract, getTokenContract } from '../utils/contractHelpers'
import { toast } from 'react-toastify'
import BigNumber from 'bignumber.js';
import { parseBytes32String } from "@ethersproject/strings";
import { getBalanceNumber } from "../utils/formatBalance"
import useRefresh from './useRefresh';
import { Context } from '../store/store';
import { isMobile } from 'react-device-detect' 
import { getPoolById } from '../services/pool' 

export const useFactoryApprove = () => {
    const { account, library, chainId } = useWeb3React()

    const handleApprove = useCallback(async (tokenAddress) => {
        try {
            if (!library || !account || !chainId) return false
            let tokenContract = getTokenContract(tokenAddress, library, account)
            if (!tokenContract) return false
            const tx = await approve(tokenContract, getAddress(chainId, 'factory'), account)
            return tx
        } catch (e: any) {
            toast.error(e.message)
            return false
        }
    }, [account, chainId])

    return { onApprove: handleApprove }
}

export const useInvest = () => {
    const { account, library, chainId } = useWeb3React()

    const handleInvest = useCallback(async (poolAddress, investAmount) => {
        try {
            if (!library || !account || !chainId) return false
            let poolContract = getPoolContract(poolAddress, chainId, library, account)
            if (!poolContract) return false
            const tx = await investPool(poolContract, investAmount)
            return tx
        } catch (e: any) {
            toast.error(e.message)
            return false
        }
    }, [account, chainId])

    return { onInvest: handleInvest }
}

export const useListingClaim = () => {
    const { account, library, chainId } = useWeb3React()

    const handleClaim = useCallback(async (poolAddress) => {
        try {
            if (!library || !account || !chainId) return false
            // let tokenContract = getTokenContract(tokenAddress, library, account)
            let poolContract = getPoolContract(poolAddress, chainId, library, account)
            if (!poolContract) return false
            const tx = await listingClaim(poolContract)
            return tx
        } catch (e: any) {
            toast.error(e.message)
            return false
        }
    }, [account, chainId])

    return { onListingClaim: handleClaim }
}

export const useVestingClaim = () => {
    const { account, library, chainId } = useWeb3React()

    const handleClaim = useCallback(async (poolAddress) => {
        try {
            if (!library || !account || !chainId) return false
            let poolContract = getPoolContract(poolAddress, chainId, library, account)
            if (!poolContract) return false
            const tx = await vestingClaim(poolContract)
            return tx
        } catch (e: any) {
            toast.error(e.message)
            return false
        }
    }, [account, chainId])

    return { onVestingClaim: handleClaim }
}

export const useIntervalProgressFetch = () => {
    const { account, library, chainId } = useWeb3React()
    const [state, dispatch] = useContext(Context);

    const fetchRaised = useCallback(async (activePool, pools) => {
        if (!account || !chainId || activePool.length <= 0) {
            dispatch({ type: 'SET_POOLS', payload: pools })
            return null
        }
        try {
            let updates: any[] = []
            for (let i = 0; i < activePool.length; i++) {
                if(activePool[i].poolAddress === "0x0000000000000000000000000000000000000000") {
                    updates.push({
                        ...activePool[i],
                        totalCollected: 0
                    })
                } else {
                    const poolContract = getPoolContract(activePool[i].poolAddress, chainId, library, account)
                    if (!poolContract) return false
                    const totalCollected = await poolContract.totalCollectedWei()
                    updates.push({
                        ...activePool[i],
                        totalCollected: getBalanceNumber(totalCollected, 18)
                    })
                }
            }
            let updatedPools = pools
            updates.forEach((updatesPool) => {
                let index = updatedPools.findIndex((pool: any) => pool.poolId === updatesPool.poolId)
                updatedPools[index] = { ...updatesPool }
            })
            dispatch({ type: 'SET_POOLS', payload: pools })
        } catch (e) {
            console.log(e)
        }
    }, [account, chainId])

    return { onFetchRaised: fetchRaised }
}

export const useIntervalPoolFetch = (poolDetails: any) => {
    const [totalCollected, setTotalCollected] = useState(poolDetails?.totalCollected ?? "0")
    const [invetment, setInvetment] = useState(poolDetails?.invetment ?? "0")
    const [claimTracker, setClaimTracker] = useState()
    const { account, library, chainId } = useWeb3React()

    const { slowRefresh } = useRefresh()

    useEffect(() => {
        const fetchAllowance = async () => {
            if (!account || !chainId || !(poolDetails && poolDetails.poolAddress)) {
                return null
            }
            try {
                const poolContract = getPoolContract(poolDetails.poolAddress, chainId, library, account)
                if (!poolContract) return false
                const totalCollected = await poolContract.totalCollectedWei()
                setTotalCollected(getBalanceNumber(totalCollected, 18))
                const invetment = await poolContract.investments(account)
                setInvetment(getBalanceNumber(invetment, 18))
                const claimTracker = await poolContract.claimTracker(account)
                setClaimTracker(claimTracker.toString())
                return {
                    ...poolDetails,
                    totalCollected,
                    invetment,
                    claimTracker
                }
            }
            catch (error) {
                console.log(error)
                return null
            }
        }

        if (account) {
            fetchAllowance()
        }
    }, [account, slowRefresh])

    if (claimTracker === undefined) {
        return poolDetails
    } else {
        return {
            ...poolDetails,
            totalCollected,
            invetment,
            claimTracker
        }
    }

}

export const usePoolFetch = () => {
    const { account, library, chainId } = useWeb3React()

    const handleFetchPoolDetails = useCallback(async (poolId: string, stakerFCFS = false, isExclusivePool) => {
        try {
            let res = await getPoolById(poolId, isExclusivePool)
            console.log(res)
            if(!res.status) throw new Error("Oops! somthing went wrong. please try again")
            let poolData = res.data.pools
            // if (!account || !library || !chainId) return false
            
            const poolAddress = poolData.poolAddress
            let totalCollectedWei = new BigNumber(0)
            let minInvestInWei = new BigNumber(0)
            let maxInvestInWei = new BigNumber(0)
            let minGuaranteedInWei = new BigNumber(0)
            let maxGuaranteedInWei = new BigNumber(0)
            let invetment = new BigNumber(0)
            let claimTracker = new BigNumber(0)
            let isRound2Eligible = false
            if(poolAddress !== "0x0000000000000000000000000000000000000000" && account && library && chainId) {
                const poolContract = getPoolContract(poolData.poolAddress, chainId, library, account)
                if (!poolContract) return false
                totalCollectedWei = await poolContract.totalCollectedWei()
                minInvestInWei = await poolContract.minInvestInWei()
                maxInvestInWei = await poolContract.maxInvestInWei()
                let minMax = await poolContract.getGuaranteedInvestAmount()
                minGuaranteedInWei = minMax[0]
                maxGuaranteedInWei = minMax[1]
                // [minGuaranteedInWei, maxGuaranteedInWei] = await poolContract.getGuaranteedInvestAmount()
                invetment = await poolContract.investments(account)
                claimTracker = await poolContract.claimTracker(account)
                if(stakerFCFS) {
                    isRound2Eligible = await poolContract.isRound2Eligible(account)
                }
            } else {
                //set default value
            }
            const tokenAddress = poolData.saleTokenAddress
            const tokenSymbol = poolData.saleTokenSymbol
            
            const tokenPriceInWei = poolData.tokenPrice
            const initialMcap = poolData.initialMcap
            const totalSupply = poolData.totalSupply
           
            const listingReleasePercent = poolData.listingReleasePercent
            const vestingWindow = poolData.vestingWindow
            const vestingIteration = poolData.vestingIteration

            const whitelistingOpenTime = Number(poolData.whitelistingOpenTime) * 1000
            const whitelistingCloseTime = Number(poolData.whitelistingCloseTime) * 1000
            const openTime = Number(poolData.openTime) * 1000
            const closeTime = Number(poolData.closeTime) * 1000
            const poolStakerFCFS = openTime + (5 * 60 * 60 * 1000)
            const poolOpenPublic = poolStakerFCFS + (3 * 60 * 60 * 1000)

            const softCapInWei = poolData.softCap
            const hardCapInWei = poolData.hardCap

            
            const listingTime = Number(poolData.listingTime) * 1000
            const projectChainId = poolData.chainId
            const chainName = poolData.chainName
            const vestingDescription = poolData.vestingDescription
            const distributionDescription = poolData.distributionDescription

            const poolCancelled = poolData.poolCancelled

            const title = poolData.title
            const description = poolData.description
            const telegram = poolData.telegram
            const twitter = poolData.twitter
            const website = poolData.website
            const logoLink = poolData.logoLink
            const pitchDeck = poolData.pitchDeck
            return {
                poolAddress,
                tokenAddress,
                tokenSymbol,
                initialMcap,
                totalSupply,
                tokenPrice: getBalanceNumber(tokenPriceInWei, 18),
                totalCollected: getBalanceNumber(totalCollectedWei, 18),
                minInvest: getBalanceNumber(minInvestInWei, 18),
                maxInvest: getBalanceNumber(maxInvestInWei, 18),
                minGuaranteed: getBalanceNumber(minGuaranteedInWei, 18),
                maxGuaranteed: getBalanceNumber(maxGuaranteedInWei, 18),
                isWhitlisted: getBalanceNumber(maxGuaranteedInWei, 18) > 0,
                invetment: getBalanceNumber(invetment, 18),
                isRound2Eligible,
                claimTracker: claimTracker.toString(),

                listingReleasePercent,
                vestingWindow,
                vestingIteration,
                vestingDescription,
                distributionDescription,

                whitelistingOpenTime: whitelistingOpenTime,
                whitelistingCloseTime: whitelistingCloseTime,
                openTime: openTime,
                closeTime: closeTime,
                poolStakerFCFS: poolStakerFCFS,
                poolOpenPublic: poolOpenPublic,
                
                softCap: getBalanceNumber(softCapInWei, 18),
                hardCap: getBalanceNumber(hardCapInWei, 6),

                listingTime: listingTime,
                projectChainId,
                chainName,
                poolCancelled,

                title: (title),
                description: (description),
                telegram: (telegram),
                twitter: (twitter),
                website: (website),
                logoLink: (logoLink),
                pitchDeck: (pitchDeck)
            }
        } catch (e: any) {
            toast.error(e.message)
            console.log(e)
        }
    }, [account, chainId])

    return { onFetchPoolDetails: handleFetchPoolDetails }
}